import { useEstabelecimento } from "@/hooks/useEstabelecimento";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { usePermissao } from "@/hooks/usePermissao";
import { ClearOutlined, FilterOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useSearchParams } from "react-router";

interface IFormValues {
  estabelecimento: {
    id: number;
  };
  dataColeta: Dayjs;
  codigo: string;
}

export function FiltroAnaliseLaboratorial() {
  const { isOnline } = useNetworkStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const { estabelecimentoOptions } = useEstabelecimento();
  const { hasEnabledPermissao } = usePermissao();
  const [form] = Form.useForm<IFormValues>();
  const { message } = App.useApp();

  const handleSubmit = (values: IFormValues) => {
    if (values.estabelecimento?.id) {
      searchParams.set("estabelecimentoId", String(values.estabelecimento.id));
    } else {
      searchParams.delete("estabelecimentoId");
    }
    if (values.dataColeta) {
      searchParams.set("dataColeta", values.dataColeta.format("DD/MM/YYYY"));
    } else {
      searchParams.delete("dataColeta");
    }
    if (values.codigo) {
      searchParams.set("codigo", values.codigo);
    } else {
      searchParams.delete("codigo");
    }

    searchParams.set("page", "1");
    setSearchParams(searchParams);

    message.success({
      content: "Filtro aplicado com sucesso",
      key: "aplicar-filtro",
    });
  };

  const handleClear = () => {
    form.resetFields();
    searchParams.delete("estabelecimentoId");
    searchParams.delete("dataColeta");
    searchParams.delete("codigo");
    searchParams.delete("page");
    setSearchParams(searchParams);

    message.info({ content: "Filtro limpo", key: "limpar-filtro" });
  };

  useEffect(() => {
    const estabelecimentoId = searchParams.get("estabelecimentoId");
    const dataColeta = searchParams.get("dataColeta");
    const codigo = searchParams.get("codigo");

    form.setFieldsValue({
      estabelecimento: {
        id: Number(estabelecimentoId) || undefined,
      },
      dataColeta: dataColeta ? dayjs(dataColeta, "DD/MM/YYYY") : undefined,
      codigo: codigo || undefined,
    });
  }, []);

  return (
    <>
      <Card
        styles={{
          body: {
            backgroundColor: "#f0f2f5",
            borderRadius: 0,
          },
          actions: {
            backgroundColor: "#fafafa",
            borderRadius: 0,
          },
        }}
        actions={[
          <Space key="actions">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              htmlType="button"
            >
              Limpar
            </Button>

            <Button
              form="form-filtro"
              type="primary"
              htmlType="submit"
              icon={<FilterOutlined />}
              disabled={!isOnline || !hasEnabledPermissao("RAL_VISUALIZAR")}
            >
              Aplicar
            </Button>
          </Space>,
        ]}
        style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}
      >
        <Card.Meta title="Filtro" />

        <Divider />

        <Form
          id="form-filtro"
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={8} xl={8}>
              <Form.Item
                name={["estabelecimento", "id"]}
                rules={[
                  { required: true, message: "Selecione um estabelecimento" },
                ]}
              >
                <Select
                  placeholder="Estabelecimento"
                  showSearch
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  optionFilterProp="label"
                  options={estabelecimentoOptions}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8} xl={8}>
              <Form.Item name="dataColeta">
                <DatePicker
                  placeholder="Data Coleta"
                  style={{ width: "100%" }}
                  format={"DD/MM/YYYY"}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8} xl={8}>
              <Form.Item name="codigo">
                <Input placeholder="Nº Requisição" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
}
